import React, { useCallback, useMemo, useState } from 'react';
import {
  ICON_BUTTON_SIZE_TB,
  IconButton,
  TOOLTIP_PLACEMENT_BOTTOM,
} from '@aibuild/ui';
import MaterialIcon from '../canvasToolbar/MaterialIcon';
import type { ValidationError, ValidationResult } from './types';
import { validationHint } from './validationHints';
import {
  Bullet,
  Copy,
  CountBadge,
  IssueItem,
  IssueList,
  ItemArrow,
  ItemCopy,
  ItemHint,
  ItemTitle,
  LegendHead,
  LegendRoot,
  LegendRow,
  SectionLabel,
  StatusIcon,
  Sub,
  Title,
} from './ProcessStatusLegend.styled';

/**
 * A run-time failure to surface in the legend alongside the static
 * graph-validation errors. Sourced from the {@code session.perNode}
 * map (live) or the cold-load seed (persisted node-state rows on
 * page refresh), so failed runs survive a hard refresh in the
 * editor — same UX as the per-node red outline + dock error banner.
 *
 * <p>The legend converts each entry into the same {@link ValidationError}
 * row shape used for graph issues so the visual treatment is identical
 * (red bullet, clickable, click → focus). The {@code RUN_FAILED} kind
 * differentiates them internally — the title copy + the legend tone
 * (error vs caution) escalate when any run failure is present.</p>
 */
export interface RunFailureItem {
  readonly nodeId: string;
  readonly nodeLabel: string;
  readonly message: string;
}

/** Sentinel {@link ValidationError#kind} used for synthesised run-failure rows. */
export const RUN_FAILED_KIND = 'RUN_FAILED';

export interface ProcessStatusLegendProps {
  /**
   * The most recent backend validation result. The legend renders
   * nothing while {@code isChecking} and there are no prior errors
   * (i.e. the first validation call hasn't returned yet) so we never
   * flash "Ready to publish" before the server has weighed in.
   */
  readonly validation: ValidationResult;
  /**
   * Failed nodes from the most recent run (live or persisted-on-refresh).
   * Rendered alongside graph-validation errors using the same row
   * component; their presence escalates the legend tone to error and
   * changes the title to "Last run failed".
   */
  readonly runFailures?: readonly RunFailureItem[];
  /** Number of nodes currently on the canvas. The legend hides on empty graphs. */
  readonly nodeCount: number;
  /** Number of edges (connections) currently on the canvas. */
  readonly connectionCount: number;
  /** Drives the OK-state label: "Ready to run" vs "Ready to publish". */
  readonly isPublished?: boolean;
  /** When true and there are no errors yet, the legend stays hidden (initial fetch). */
  readonly isChecking?: boolean;
  /**
   * Called when the user clicks an issue row that attaches to a node.
   * The page handles the actual focus gesture (centre + select on
   * canvas) — the legend just sources the click.
   */
  readonly onFocusNode?: (nodeId: string) => void;
  /**
   * Edge equivalent of {@link onFocusNode}. Fires for {@code PORT_TYPE_MISMATCH}
   * and the {@code EDGE_*} integrity rules.
   */
  readonly onFocusEdge?: (edgeId: string) => void;
  /**
   * Optional override for the initial expanded state. When omitted the
   * panel auto-expands whenever issues are present (matching the
   * design's "nudge the user towards the fix" behaviour).
   */
  readonly defaultExpanded?: boolean;
  readonly dataTestId?: string;
}

const DEFAULT_TEST_ID = 'process-status-legend';

const pluralize = (count: number, noun: string): string =>
  count === 1 ? `${count} ${noun}` : `${count} ${noun}s`;

const okSub = (nodeCount: number, connectionCount: number): string =>
  `${pluralize(nodeCount, 'node')} \u00B7 ${pluralize(connectionCount, 'connection')}`;

/**
 * OK-state subtitle, with a trailing heads-up count when the graph is
 * publishable but carries advisory warnings (e.g. a local node whose
 * assignees have no capable runner yet).
 */
const okSubWithWarnings = (
  nodeCount: number,
  connectionCount: number,
  warningCount: number,
): string => {
  const base = okSub(nodeCount, connectionCount);
  if (warningCount === 0) return base;
  return `${base} \u00B7 ${warningCount} heads-up`;
};

interface WarnSubInput {
  readonly failureCount: number;
  readonly validationCount: number;
}

/**
 * Subtitle copy under the legend title in the WARN state. Tuned per-mix:
 * when there are only graph-validation issues we keep the existing
 * "individual nodes still runnable" reassurance (graph compiles per-node,
 * just not end-to-end). When a runtime failure is in the mix that
 * reassurance is misleading — the run actually FAILED on a specific node
 * — so we drop it and lead with the concrete failure count.
 */
const warnSub = ({ failureCount, validationCount }: WarnSubInput): string => {
  if (failureCount === 0) {
    return `${pluralize(validationCount, 'thing')} to fix \u00B7 individual nodes still runnable`;
  }
  if (validationCount === 0) {
    return `${pluralize(failureCount, 'node')} failed in the last run`;
  }
  return `${pluralize(failureCount, 'node')} failed \u00B7 ${pluralize(validationCount, 'validation issue')}`;
};

const okTitle = (isPublished: boolean): string =>
  isPublished ? 'Ready to run' : 'Ready to publish';

interface ResolveTitleInput {
  readonly hasFailure: boolean;
  readonly failureCount: number;
  readonly isWarn: boolean;
  readonly publishBlockerCount: number;
  readonly isPublished: boolean;
}

/**
 * Header copy for the legend. Failures win the slot when present —
 * they're the more actionable signal (an actual run blew up here) and
 * we want the user's eye drawn to "fix this red node" rather than
 * "your graph has 3 amber warnings". When no failure is present we fall
 * back to the existing validation copy / ok copy.
 */
function resolveTitle({
  hasFailure,
  failureCount,
  isWarn,
  publishBlockerCount,
  isPublished,
}: ResolveTitleInput): string {
  if (hasFailure) {
    return failureCount === 1
      ? 'Last run failed on 1 node'
      : `Last run failed on ${failureCount} nodes`;
  }
  if (isWarn) return 'Not runnable end-to-end';
  if (publishBlockerCount > 0) {
    return `Runnable \u00B7 ${pluralize(publishBlockerCount, 'item')} needed before publishing`;
  }
  return okTitle(isPublished);
}

/**
 * Pick the legend tone band. Runtime failures escalate to {@code 'error'}
 * (red — something actually broke), graph-validation drops to
 * {@code 'caution'} (amber — fixable lint), otherwise {@code 'success'}.
 * A draft that only misses publish-time wiring stays {@code 'success'}:
 * it runs today, so nothing is wrong yet.
 */
function resolveTone(
  hasFailure: boolean,
  isWarn: boolean,
): 'error' | 'caution' | 'success' {
  if (hasFailure) return 'error';
  if (isWarn) return 'caution';
  return 'success';
}

/** A tick would over-claim on a draft that still needs wiring to publish. */
function resolveIconName(isWarn: boolean, publishBlockerCount: number): string {
  if (isWarn) return 'error';
  return publishBlockerCount > 0 ? 'info' : 'check_circle';
}

/**
 * Resolve the click affordance for an issue row. Returns {@code null}
 * when the issue doesn't attach to anything we can focus (e.g.
 * graph-level rules like {@code MISSING_INPUT_NODE}).
 */
function resolveFocus(
  error: ValidationError,
  onFocusNode: ((nodeId: string) => void) | undefined,
  onFocusEdge: ((edgeId: string) => void) | undefined,
): (() => void) | null {
  if (error.nodeId && onFocusNode) {
    const id = error.nodeId;
    return () => onFocusNode(id);
  }
  if (error.edgeId && onFocusEdge) {
    const id = error.edgeId;
    return () => onFocusEdge(id);
  }
  return null;
}

/**
 * Floating status legend pinned to the top-center of the canvas, below
 * the floating top toolbar. Surfaces the aggregate run-readiness of the
 * graph and — when there are validation errors — expands into a click-
 * to-locate list of each issue.
 *
 * <p>The topbar's {@code SaveStatusIndicator} pill already carries the
 * "Saved \u00B7 N issues" at-a-glance signal; this legend is the
 * companion "and here is what each of those issues is" surface so the
 * user can act on them without hovering every node's run button.</p>
 *
 * <p>Hidden when the canvas is empty (the React-Flow empty-state CTA
 * already explains what to do) AND when the initial detail fetch
 * hasn't returned yet (we don't want to flash a misleading "Ready to
 * publish" before the server has weighed in).</p>
 */
const EMPTY_RUN_FAILURES: readonly RunFailureItem[] = [];

/**
 * Aggregate failure + validation rows into the single render-ready list
 * the legend body iterates. Extracted so the main component's cognitive
 * complexity stays under the SonarJS budget — three useMemos in the
 * body would tip it over the threshold.
 */
function useAggregatedErrors(
  validation: ValidationResult,
  runFailures: readonly RunFailureItem[],
): { failureErrors: readonly ValidationError[]; errors: readonly ValidationError[] } {
  const failureErrors = useMemo<readonly ValidationError[]>(
    () => runFailures.map((rf) => ({
      nodeId: rf.nodeId,
      edgeId: null,
      portId: null,
      kind: RUN_FAILED_KIND,
      message: rf.nodeLabel ? `${rf.nodeLabel}: ${rf.message}` : rf.message,
    })),
    [runFailures],
  );
  const errors = useMemo<readonly ValidationError[]>(
    () => [...failureErrors, ...validation.errors],
    [failureErrors, validation.errors],
  );
  return { failureErrors, errors };
}

interface LegendActionsProps {
  readonly isWarn: boolean;
  readonly hasFailure: boolean;
  readonly errorCount: number;
  /** Non-blocking count shown when there are no blocking errors. */
  readonly adviceCount: number;
  readonly expanded: boolean;
  readonly onToggleExpand: () => void;
  readonly dataTestId: string;
}

/**
 * Right-hand side of the legend row: the issue-count badge + expand
 * toggle + dismiss button. Extracted to flatten the main render so
 * Sonar's cognitive-complexity metric stays in budget, and to keep
 * the WARN/OK branching localised to one component.
 */
const LegendActions: React.FC<LegendActionsProps> = ({
  isWarn,
  hasFailure,
  errorCount,
  adviceCount,
  expanded,
  onToggleExpand,
  dataTestId,
}) => {
  if (!isWarn && adviceCount === 0) return null;
  const badgeCount = isWarn ? errorCount : adviceCount;
  const badgeTone = hasFailure ? 'error' : 'caution';
  return (
    <>
      <CountBadge
        $tone={badgeTone}
        aria-hidden="true"
        data-testid={`${dataTestId}__count`}
      >
        {badgeCount}
      </CountBadge>
      <IconButton
        ariaLabel={expanded ? 'Hide issues' : 'Show issues'}
        tooltip={expanded ? 'Hide issues' : 'Show issues'}
        tooltipPlacement={TOOLTIP_PLACEMENT_BOTTOM}
        size={ICON_BUTTON_SIZE_TB}
        icon={<MaterialIcon name={expanded ? 'expand_less' : 'expand_more'} />}
        onClick={onToggleExpand}
        aria-expanded={expanded}
        dataTestId={`${dataTestId}__toggle`}
      />
    </>
  );
};

interface LegendIssueListProps {
  readonly errors: readonly ValidationError[];
  /** Rendered below a divider — they gate Publish, not Run. */
  readonly publishBlockers: readonly ValidationError[];
  readonly onFocusNode?: (nodeId: string) => void;
  readonly onFocusEdge?: (edgeId: string) => void;
  readonly dataTestId: string;
}

/**
 * The expanded {@code <IssueList>} containing every error row.
 * Lifted out of the main component so the {@code errors.map} +
 * key composition lives outside the parent's complexity budget.
 */
const LegendIssueList: React.FC<LegendIssueListProps> = ({
  errors,
  publishBlockers,
  onFocusNode,
  onFocusEdge,
  dataTestId,
}) => (
  <IssueList
    role="list"
    data-testid={`${dataTestId}__list`}
  >
    {errors.map((error, index) => (
      <IssueRow
        // Backend rules emit at most one error per (rule, nodeId,
        // portId, edgeId) tuple, but graph-level errors share a
        // null nodeId — fall back to the array index so the keys
        // stay unique even when two graph rules fire together.
        key={rowKey(error, index)}
        error={error}
        onFocusNode={onFocusNode}
        onFocusEdge={onFocusEdge}
        dataTestId={`${dataTestId}__item-${index}`}
      />
    ))}
    {publishBlockers.length > 0 ? (
      <SectionLabel data-testid={`${dataTestId}__publish-section`}>
        Needed before publishing
      </SectionLabel>
    ) : null}
    {publishBlockers.map((error, index) => (
      <IssueRow
        key={`publish:${rowKey(error, index)}`}
        error={error}
        onFocusNode={onFocusNode}
        onFocusEdge={onFocusEdge}
        dataTestId={`${dataTestId}__publish-item-${index}`}
      />
    ))}
  </IssueList>
);

const rowKey = (error: ValidationError, index: number): string =>
  `${error.kind}:${error.nodeId ?? ''}:${error.portId ?? ''}:${error.edgeId ?? ''}:${index}`;

const ProcessStatusLegend: React.FC<ProcessStatusLegendProps> = ({
  validation,
  runFailures = EMPTY_RUN_FAILURES,
  nodeCount,
  connectionCount,
  isPublished = false,
  isChecking = false,
  onFocusNode,
  onFocusEdge,
  defaultExpanded,
  dataTestId = DEFAULT_TEST_ID,
}) => {
  // Synthesise ValidationError rows for run failures so the existing
  // IssueRow renderer can show them with no branching. They're rendered
  // FIRST (above the static validation errors) because a runtime failure
  // is a more pressing signal — the graph compiled, it ran, and a
  // specific node blew up; fixing it is more concrete than chasing
  // schema-level warnings.
  const { failureErrors, errors } = useAggregatedErrors(validation, runFailures);
  const errorCount = errors.length;
  const validationCount = validation.errors.length;
  const failureCount = failureErrors.length;
  const warnings = validation.warnings;
  const warningCount = warnings.length;
  const publishBlockers = validation.publishBlockers;
  const publishBlockerCount = publishBlockers.length;
  const isWarn = errorCount > 0;
  const hasFailure = failureCount > 0;
  // Neither advisory warnings nor publish blockers make the graph "not
  // runnable", but both open the list so the author can read them
  // (rendered below the blocking rows). Such graphs stay in the OK tone.
  const adviceCount = warningCount + publishBlockerCount;
  const hasList = isWarn || adviceCount > 0;

  // The pill is collapsed by default — the user opens the list on
  // demand via the caret. Auto-expanding when issues appear makes the
  // panel hostile during normal authoring (it pops open every time a
  // wire is half-connected) and overrides any prior user collapse.
  // The {@code defaultExpanded} prop remains as an explicit override
  // for stories / tests that drive the open list directly.
  const [expanded, setExpanded] = useState<boolean>(
    () => defaultExpanded ?? false,
  );

  const onToggleExpand = useCallback(() => {
    setExpanded((o) => !o);
  }, []);

  // Hide on empty canvas — the React-Flow empty CTA already covers
  // that surface, no point stacking two messages.
  if (nodeCount === 0) return null;
  // Suppress until the initial validation fetch settles. Once we have
  // any non-empty result the legend stays visible across subsequent
  // checks (we just keep showing the last-good state).
  if (isChecking && errorCount === 0 && adviceCount === 0 && validation.valid) {
    return null;
  }

  // Failures escalate the tone to "error" (red), validation-only stays at
  // "caution" (amber). The StatusIcon styled-component is responsible for
  // mapping the data-tone attribute to the right theme token; we just
  // pass the semantic tier.
  const tone = resolveTone(hasFailure, isWarn);
  const iconName = resolveIconName(isWarn, publishBlockerCount);
  const title = resolveTitle({
    hasFailure,
    failureCount,
    isWarn,
    publishBlockerCount,
    isPublished,
  });
  const subtitle = isWarn
    ? warnSub({ failureCount, validationCount })
    : okSubWithWarnings(nodeCount, connectionCount, warningCount);
  const showIssueList = expanded && hasList;

  return (
    <LegendRoot
      $expanded={showIssueList}
      role="status"
      aria-live="polite"
      data-testid={dataTestId}
      data-tone={tone}
      data-expanded={showIssueList ? 'true' : 'false'}
    >
      <LegendRow>
        <LegendHead>
          <StatusIcon
            $tone={tone}
            data-testid={`${dataTestId}__icon`}
            aria-hidden="true"
          >
            <MaterialIcon name={iconName} />
          </StatusIcon>
          <Copy>
            <Title data-testid={`${dataTestId}__title`}>{title}</Title>
            <Sub data-testid={`${dataTestId}__sub`}>{subtitle}</Sub>
          </Copy>
        </LegendHead>
        <LegendActions
          isWarn={isWarn}
          hasFailure={hasFailure}
          errorCount={errorCount}
          adviceCount={adviceCount}
          expanded={expanded}
          onToggleExpand={onToggleExpand}
          dataTestId={dataTestId}
        />
      </LegendRow>
      {showIssueList ? (
        <LegendIssueList
          errors={[...errors, ...warnings]}
          publishBlockers={publishBlockers}
          onFocusNode={onFocusNode}
          onFocusEdge={onFocusEdge}
          dataTestId={dataTestId}
        />
      ) : null}
    </LegendRoot>
  );
};

interface IssueRowProps {
  readonly error: ValidationError;
  readonly onFocusNode?: (nodeId: string) => void;
  readonly onFocusEdge?: (edgeId: string) => void;
  readonly dataTestId: string;
}

/**
 * Single row in the expanded issue list. Renders the backend message as
 * the primary line and a per-kind hint (when known) as the secondary
 * line. Clickable when the error attaches to a focusable target
 * (node or edge); inert when the error is graph-level (e.g. "process
 * is missing an Input node") since there's nothing to centre on.
 */
const IssueRow: React.FC<IssueRowProps> = ({
  error,
  onFocusNode,
  onFocusEdge,
  dataTestId,
}) => {
  const focus = resolveFocus(error, onFocusNode, onFocusEdge);
  const hint = validationHint(error.kind);
  const onKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLLIElement>) => {
      if (!focus) return;
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        focus();
      }
    },
    [focus],
  );

  return (
    <IssueItem
      role={focus ? 'button' : 'listitem'}
      tabIndex={focus ? 0 : -1}
      aria-disabled={!focus}
      onClick={focus ?? undefined}
      onKeyDown={onKeyDown}
      data-testid={dataTestId}
      data-kind={error.kind}
      title={focus ? 'Click to locate on the canvas' : undefined}
    >
      <Bullet
        $tone={error.kind === RUN_FAILED_KIND ? 'error' : 'caution'}
        aria-hidden="true"
      />
      <ItemCopy>
        <ItemTitle>{error.message}</ItemTitle>
        {hint ? <ItemHint>{hint}</ItemHint> : null}
      </ItemCopy>
      {focus ? (
        <ItemArrow aria-hidden="true">
          <MaterialIcon name="arrow_forward" />
        </ItemArrow>
      ) : null}
    </IssueItem>
  );
};

export default ProcessStatusLegend;
